import React from "react";
import { useNavigate, Link, Navigate, useLocation } from "react-router-dom";
import { Button, Form, Container, Row, Col } from "react-bootstrap";
import { Formik } from "formik";
import { ToastContainer, toast } from "react-toastify";
import { Helmet } from "react-helmet";
import { useAuth } from "../AuthUser";
import API from "../api";
import { trackEvent } from "../analytics";

// Referral key the signup endpoint recognizes to grant 1 free swing-review credit.
// Must match the page route (/free) AND the backend allow-list in
// api/server/api/controllers/auth.js (signUpController).
const REFERRAL = "/free";

// The one <option> value that is not a country: it opens a text field
// instead, so it is referenced from the list, the validation and the
// payload and must stay a single spelling.
const OTHER_COUNTRY = "OTHER";

// Main countries where golf is popular. The <option> value is the standard
// ISO 3166-1 alpha-2 code (what we send to the backend); the label is the
// display name. United States is pinned first and is the default; the rest are
// sorted alphabetically by name, with "Other" (sentinel "OTHER") kept last.
const GOLF_COUNTRIES = [
	{ code: "US", name: "United States" },
	{ code: "AR", name: "Argentina" },
	{ code: "AU", name: "Australia" },
	{ code: "AT", name: "Austria" },
	{ code: "BE", name: "Belgium" },
	{ code: "CA", name: "Canada" },
	{ code: "CN", name: "China" },
	{ code: "CZ", name: "Czechia" },
	{ code: "DK", name: "Denmark" },
	{ code: "FI", name: "Finland" },
	{ code: "FR", name: "France" },
	{ code: "DE", name: "Germany" },
	{ code: "IN", name: "India" },
	{ code: "ID", name: "Indonesia" },
	{ code: "IE", name: "Ireland" },
	{ code: "IT", name: "Italy" },
	{ code: "JP", name: "Japan" },
	{ code: "MX", name: "Mexico" },
	{ code: "NL", name: "Netherlands" },
	{ code: "NZ", name: "New Zealand" },
	{ code: "NO", name: "Norway" },
	{ code: "PH", name: "Philippines" },
	{ code: "SG", name: "Singapore" },
	{ code: "ZA", name: "South Africa" },
	{ code: "KR", name: "South Korea" },
	{ code: "ES", name: "Spain" },
	{ code: "SE", name: "Sweden" },
	{ code: "CH", name: "Switzerland" },
	{ code: "TH", name: "Thailand" },
	{ code: "AE", name: "United Arab Emirates" },
	{ code: "GB", name: "United Kingdom" },
	{ code: OTHER_COUNTRY, name: "Other" },
];

const FreeSwingReview = () => {
	const { user, setUser } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();

	if (user) {
		return <Navigate to="/dna-profile" replace />;
	}

	const notify = (message) =>
		toast.error(message, {
			position: toast.POSITION.TOP_RIGHT,
		});

	return (
		<>
			<Helmet>
				<title>Free Swing Review | MyGolfDNA.com</title>
				<meta
					name="description"
					content="Free swing review from Master Instructor Chris Tyler. Serious Golfers. Serious Instruction."
				/>
			</Helmet>

			<Container className="my-5">
				<Row className="justify-content-center text-center">
					<Col lg={9}>
						<h1 className="display-4 fw-bold text-uppercase">
							Get a <span className="text-secondary">Free Swing Review</span> from
							Master Instructor Chris Tyler
						</h1>
						<p className="lead text-muted mx-auto mt-3 mb-5" style={{ maxWidth: "680px" }}>
							Upload a video of your swing and get a personalized video breakdown within a
							few days. No guessing. No generic YouTube tips. Just clear instruction
							tailored to your swing.
						</p>
					</Col>
				</Row>

				<Row className="justify-content-center">
					<Col lg={9}>
						<div
							className="ratio ratio-16x9 rounded overflow-hidden shadow"
							style={{ background: "#000" }}
						>
							<iframe
								title="Free Swing Review"
								src="https://player.vimeo.com/video/1197712077?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479"
								frameBorder="0"
								allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
								referrerPolicy="strict-origin-when-cross-origin"
								allowFullScreen
							/>
						</div>
					</Col>
				</Row>

				<Row className="justify-content-center mt-5">
					<Col sm={12} md={8} lg={6} xl={5}>
						<div className="border rounded p-4 shadow-sm">
							<h2 className="h4 text-center mb-4">Create your free account</h2>
							<Formik
								initialValues={{
									firstName: "",
									lastName: "",
									email: "",
									password: "",
									country: "US",
									countryOther: "",
									handed: "",
								}}
								validate={(values) => {
									const errors = {};
									if (!values.firstName) {
										errors.firstName = "Required";
									}
									if (!values.lastName) {
										errors.lastName = "Required";
									}
									if (!values.email) {
										errors.email = "Required";
									} else if (
										!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
									) {
										errors.email = "Invalid email address";
									}
									if (!values.password) {
										errors.password = "Required";
									} else if (values.password.length < 8) {
										errors.password = "Password must be at least 8 characters";
									}
									if (!values.country) {
										errors.country = "Required";
									}
									// Picking "Other" and leaving the box empty tells us less than the
									// select alone did, so it is the one case worth blocking on.
									if (
										values.country === OTHER_COUNTRY &&
										!values.countryOther.trim()
									) {
										errors.countryOther = "Required";
									}
									if (!values.handed) {
										errors.handed = "Please select one";
									}
									return errors;
								}}
								onSubmit={(values, { setSubmitting, setErrors }) => {
									// country is the ISO 3166-1 alpha-2 code (or the "OTHER" sentinel,
									// in which case countryOther carries what they typed); handed is
									// "right" | "left". All three require matching support in the
									// signup API (Joi schema + Account.country / Account.countryOther /
									// Account.handed columns).
									API.post(`auth/signup?r=${REFERRAL}`, {
										firstName: values.firstName,
										lastName: values.lastName,
										email: values.email,
										password: values.password,
										country: values.country,
										// Only meaningful next to "Other"; anything left in the field
										// after switching back to a listed country is stale.
										countryOther:
											values.country === OTHER_COUNTRY
												? values.countryOther.trim()
												: "",
										handed: values.handed,
									})
										.then((res) => {
											setUser(res.data);
											trackEvent("sign_up", { method: "free-swing-review" });
											if (res.data.canSubmitLesson) {
												return navigate(`/upload-videos?r=${REFERRAL}`, {
													replace: true,
												});
											}
											navigate(REFERRAL, { replace: true });
											setSubmitting(false);
										})
										.catch((error) => {
											setSubmitting(false);
											if (error.response) {
												notify(error.response.data.message);
											}
											if (
												error.response &&
												error.response.data.errors &&
												error.response.data.errors.length
											) {
												const result = error.response.data.errors.reduce(
													(map, obj) => {
														map[obj.property] = obj.message;
														return map;
													},
													{}
												);
												setErrors(result);
											}
										});
								}}
							>
								{({
									values,
									errors,
									touched,
									handleChange,
									handleBlur,
									handleSubmit,
									isSubmitting,
								}) => (
									<Form noValidate onSubmit={handleSubmit}>
										<Row>
											<Col xs={12} sm={6}>
												<Form.Group className="mb-3">
													<Form.Control
														type="text"
														name="firstName"
														placeholder="First name*"
														value={values.firstName}
														onChange={handleChange}
														onBlur={handleBlur}
														isInvalid={touched.firstName && !!errors.firstName}
													/>
													<Form.Control.Feedback type="invalid">
														{errors.firstName}
													</Form.Control.Feedback>
												</Form.Group>
											</Col>
											<Col xs={12} sm={6}>
												<Form.Group className="mb-3">
													<Form.Control
														type="text"
														name="lastName"
														placeholder="Last name*"
														value={values.lastName}
														onChange={handleChange}
														onBlur={handleBlur}
														isInvalid={touched.lastName && !!errors.lastName}
													/>
													<Form.Control.Feedback type="invalid">
														{errors.lastName}
													</Form.Control.Feedback>
												</Form.Group>
											</Col>
										</Row>
										<Form.Group className="mb-3">
											<Form.Control
												type="email"
												name="email"
												placeholder="Email address*"
												value={values.email}
												onChange={handleChange}
												onBlur={handleBlur}
												isInvalid={touched.email && !!errors.email}
											/>
											<Form.Control.Feedback type="invalid">
												{errors.email}
											</Form.Control.Feedback>
										</Form.Group>
										<Form.Group className="mb-3">
											<Form.Control
												type="password"
												name="password"
												placeholder="Create a password*"
												value={values.password}
												onChange={handleChange}
												onBlur={handleBlur}
												isInvalid={touched.password && !!errors.password}
											/>
											<Form.Control.Feedback type="invalid">
												{errors.password}
											</Form.Control.Feedback>
										</Form.Group>
										<Form.Group className="mb-3">
											<Form.Select
												id="country"
												name="country"
												value={values.country}
												onChange={handleChange}
												onBlur={handleBlur}
												isInvalid={touched.country && !!errors.country}
												aria-label="Country"
											>
												{GOLF_COUNTRIES.map((c) => (
													<option key={c.code} value={c.code}>
														{c.name}
													</option>
												))}
											</Form.Select>
											<Form.Control.Feedback type="invalid">
												{errors.country}
											</Form.Control.Feedback>
										</Form.Group>
										{values.country === OTHER_COUNTRY && (
											<Form.Group className="mb-3">
												<Form.Control
													type="text"
													id="countryOther"
													name="countryOther"
													placeholder="Your country*"
													value={values.countryOther}
													onChange={handleChange}
													onBlur={handleBlur}
													isInvalid={
														touched.countryOther && !!errors.countryOther
													}
													aria-label="Your country"
													autoComplete="country-name"
													maxLength={100}
												/>
												<Form.Control.Feedback type="invalid">
													{errors.countryOther}
												</Form.Control.Feedback>
											</Form.Group>
										)}
										<Form.Group className="mb-3">
												<Form.Label className="d-block mb-2">
													Do you golf right or left handed?
												</Form.Label>
												<Form.Check
													inline
													type="radio"
													id="handed-right"
													name="handed"
													label="Right handed"
													value="right"
													checked={values.handed === "right"}
													onChange={handleChange}
													onBlur={handleBlur}
												/>
												<Form.Check
													inline
													type="radio"
													id="handed-left"
													name="handed"
													label="Left handed"
													value="left"
													checked={values.handed === "left"}
													onChange={handleChange}
													onBlur={handleBlur}
												/>
												{touched.handed && errors.handed && (
													<div className="text-danger small mt-1">
														{errors.handed}
													</div>
												)}
											</Form.Group>
											<div className="d-grid">
											<Button
												variant="secondary"
												size="lg"
												type="submit"
												disabled={isSubmitting}
											>
												{isSubmitting ? (
													<>
														<span
															className="spinner-border spinner-border-sm"
															role="status"
															aria-hidden={true}
														/>{" "}
													</>
												) : (
													"Sign up for FREE"
												)}
											</Button>
										</div>
										<p className="text-center text-muted small mt-3 mb-0">
											Takes 30 seconds. No credit card required.
										</p>
										<p className="text-center text-muted small mt-2 mb-0">
											Already have an account?{" "}
											<Link to="/signin" state={{ from: location }}>
												Log in
											</Link>
										</p>
									</Form>
								)}
							</Formik>
						</div>
					</Col>
				</Row>
			</Container>

			<ToastContainer />
		</>
	);
};

export default FreeSwingReview;
